import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, ScrollView, Image, Platform, ActivityIndicator, Alert } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores } from '../../../../styles/variablesCss';
import { BACKEND_URL } from '../../../../Config/Const';
import { useCart } from '../../../../context/CartContext';
import { useUser } from '../../../../context/UserContext';
import Storage from '../../../../utils/Storage';
import Login from '../../Header/AdminUsuario/Login';
import FeedbackModal from '../../../Common/FeedbackModal';

/**
 * Modal para mostrar el producto de regalo que viene con el combo
 * @param {Object} props - Component props
 * @param {boolean} visible - Si el modal está visible
 * @param {function} onClose - Función para cerrar el modal
 * @param {Object} combo - Información del combo con pluRegalo
 * @param {Object} producto - Información del producto principal
 */
const ComboPrecioFijo = ({ visible, onClose, combo, producto }) => {
  // Función de sanitización para evitar renderizar valores inválidos
  const safeText = (value, defaultValue = '') => {
    if (!value || value === '.' || value === null || value === undefined) return defaultValue;
    const stringValue = String(value).trim();
    if (stringValue === '' || stringValue === '.') return defaultValue;
    return stringValue;
  };

  // Estados para manejar la carga de productos del combo
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Estado para controlar el modal de login
  const [showLogin, setShowLogin] = useState(false);
  // Estado para controlar si se está agregando al carrito
  const [agregandoCarrito, setAgregandoCarrito] = useState(false);
  // Estado para la cantidad de combos a agregar
  const [cantidad, setCantidad] = useState(1);
  
  // Estados para el modal de feedback
  const [feedbackModal, setFeedbackModal] = useState({
    visible: false,
    type: 'error',
    title: '',
    message: ''
  });
  

  // Obtener funciones del contexto del carrito y usuario
  const { cargarProductosCarrito } = useCart();
  const { codUsu, mail } = useUser();

  // Función para extraer solo el precio del descom (ej: "2 x $18990" -> "$18990")
  const getPrecioCombo = () => {
    const descomSafe = safeText(combo?.descom, 'COMBO');
    if (descomSafe === 'COMBO') return 'COMBO';
    const match = descomSafe.match(/\$[\d.,]+/);
    return match ? match[0] : descomSafe;
  };

  // Función para obtener el cantOblig de un producto desde el array combo del producto
  const getCantOblig = (plu) => {
    if (producto && producto.combo && Array.isArray(producto.combo)) {
      const comboItem = producto.combo.find(c => c.pluAsoc === plu);
      return comboItem?.cantOblig || 1;
    }
    return 1;
  };

  // Funciones para controlar la cantidad
  const incrementarCantidad = () => {
    setCantidad(prev => prev + 1);
  };

  const decrementarCantidad = () => {
    setCantidad(prev => prev > 1 ? prev - 1 : 1);
  };
  // Función para cargar los productos del combo desde la API
  const loadGiftProduct = async () => {
    try {
      setLoading(true);
      setError(null);

      // Obtener la sucursal del localStorage
      const sucursalId = await Storage.getItem('sucursalId');
      const suc = sucursalId;

      // Construir la URL con el número de combo
      const apiUrl = `${BACKEND_URL}/rest/productos/combinables?nroCombo=${combo.nroCombo}&suc=${suc}`;

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`Error al cargar productos del combo: ${response.status}`);
      }

      const data = await response.json();

      // Verificar que la respuesta sea correcta
      if (data && data.dsResultado && data.dsResultado.productos && data.dsResultado.productos.length > 0) {
        setProductos(data.dsResultado.productos);
      } else {
        setProductos([]);
      }
    } catch (err) {
      console.error('Error al cargar productos del combo:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Efecto para cargar los productos del combo cuando el modal se abre
  useEffect(() => {
    if (visible && combo && combo.nroCombo) {
      loadGiftProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, combo]);

  // Responsive: detectar si la pantalla web es pequeña para ampliar el ancho a 90%
  const [viewportWidth, setViewportWidth] = useState(
    Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerWidth : 1000
  );
  const isSmallWeb = Platform.OS === 'web' && viewportWidth < 768;

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const onResize = () => setViewportWidth(window.innerWidth);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
  }, []);

  // Función para agregar el producto con regalo al carrito
  const agregarProductoConRegalo = async () => {
    try {
      // Verificar login requerido: codUsu y mail
      if (!codUsu || !mail) {
        setShowLogin(true);
        return;
      }

      setAgregandoCarrito(true);

      // Validar que haya productos para agregar
      if (!productos || productos.length === 0) {
        console.error('No hay productos para agregar al carrito');
        setFeedbackModal({
          visible: true,
          type: 'error',
          title: 'Productos no combinables',
          message: 'No hay productos disponibles en este combo'
        });
        return;
      }

      // Agregar TODOS los productos del combo con la cantidad obligatoria × cantidad de combos
      const productosParaAgregar = productos.map(prod => {
        const cantOblig = getCantOblig(prod.plu);
        return {
          plu: prod.plu,
          cant: cantOblig * cantidad
        };
      });

      // Llamar a la API para agregar el producto
      const { mail: userMail, codUsu: userCodUsu } = { mail, codUsu };
      
      // Obtener la sucursal del localStorage
      const sucursalId = await Storage.getItem('sucursalId');
      const suc = sucursalId;

      const queryParams = new URLSearchParams({
        mail: userMail || '',
        codUsu: userCodUsu || '',
        suc: suc
      }).toString();

      const body = {
        ttCarrito: productosParaAgregar
      };

      const apiUrl = `${BACKEND_URL}/rest/productos/carrito?${queryParams}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const responseText = await response.text();
      let data;

      try {
        data = JSON.parse(responseText);
      } catch (e) {
        data = { message: responseText };
      }

      // Verificar si hay un error en la respuesta (resultado: false)
      if (data && data.dsResultado && data.dsResultado.resultado && data.dsResultado.resultado.length > 0) {
        const resultado = data.dsResultado.resultado[0];
        
        if (resultado.resultado === false) {
          // Mostrar el modal con el mensaje de error del servidor
          setFeedbackModal({
            visible: true,
            type: 'error',
            title: 'Productos no combinables',
            message: resultado.mensaje || 'No se pueden mezclar estos productos en el carrito'
          });
          return;
        }
      }

      // Si llegamos aquí, los productos se agregaron exitosamente
      if (response.ok) {
        // Recargar el carrito desde el contexto
        await cargarProductosCarrito();

        // Mostrar mensaje de éxito
        if (Platform.OS !== 'web') {
          Alert.alert(
            '¡Combo agregado!',
            `Se agregaron ${productosParaAgregar.length} productos al carrito.`,
            [{ text: 'OK' }]
          );
        }

        // Cerrar el modal
        onClose();

        // Disparar evento para actualizar el carrito en otros componentes
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const event = new CustomEvent('actualizarCarrito');
          window.dispatchEvent(event);
        }
      } else {
        const errorMsg = (data && data.message) || `Error ${response.status}: No se pudo agregar el producto al carrito.`;
        console.error('Error al agregar producto:', errorMsg);
        
        setFeedbackModal({
          visible: true,
          type: 'error',
          title: 'Productos no combinables',
          message: errorMsg
        });
      }
    } catch (error) {
      console.error('Error al agregar producto con regalo:', error);
      setFeedbackModal({
        visible: true,
        type: 'error',
        title: 'Productos no combinables',
        message: 'No se pudo agregar el producto al carrito. Los productos pueden no ser compatibles.'
      });
    } finally {
      setAgregandoCarrito(false);
    }
  };

  // Si no hay combo o producto, no mostrar nada
  if (!combo || !producto) {
    return null;
  }

  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={visible}
      onRequestClose={onClose}
    >
      <View style={styles.centeredView}>
        <View style={[
          styles.modalView,
          Platform.OS === 'web' && isSmallWeb ? { width: '90%' } : null
        ]}>
          {/* Header del modal */}
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{getPrecioCombo()}</Text>
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <MaterialCommunityIcons name="close" size={24} color={colores.blanco} />
            </TouchableOpacity>
          </View>

          {/* Información de los productos del combo */}
          <ScrollView style={styles.scrollContainer}>
            <Text style={styles.sectionTitle}>Productos incluidos en el combo</Text>

            {/* Mensaje de error si hay */}
            {error && (
              <View style={styles.messageContainer}>
                <MaterialCommunityIcons name="alert-circle-outline" size={40} color={colores.rojo} />
                <Text style={styles.errorText}>{error}</Text>
                <TouchableOpacity style={styles.retryButton} onPress={loadGiftProduct}>
                  <Text style={styles.retryButtonText}>Reintentar</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Indicador de carga */}
            {loading && (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colores.rojo} />
                <Text style={styles.loadingText}>Cargando productos del combo...</Text>
              </View>
            )}

            {/* Mensaje si no hay productos */}
            {!loading && !error && productos.length === 0 && (
              <View style={styles.messageContainer}>
                <MaterialCommunityIcons name="information-outline" size={40} color={colores.grisOscuro} />
                <Text style={styles.infoText}>
                  No se encontraron productos para este combo.
                </Text>
              </View>
            )}

            {/* Mostrar todos los productos del combo en horizontal */}
            {!loading && !error && productos.length > 0 && (
              <View style={styles.giftProductContainer}>
                {/* Banner único con la descripción del combo, ocupando todo el ancho */}
                <View style={styles.comboWideBadge}>
                  <Text style={styles.comboWideBadgeText}>{getPrecioCombo()}</Text>
                </View>

                <View style={[
                  styles.horizontalProductsContainer,
                  isSmallWeb && styles.verticalProductsContainer
                ]}>
                  {productos.map((prod, index) => {
                    // Determinar si es el producto de regalo
                    const esRegalo = prod.plu === combo.pluRegalo;
                    
                    return (
                      <React.Fragment key={prod.plu}>
                        {/* Producto */}
                        <View style={[
                          styles.productCardHorizontal,
                          isSmallWeb && styles.productCardVertical
                        ]}>
                          {/* Banner por producto eliminado; usamos uno general arriba */}

                          {/* Imagen del producto */}
                          {prod.foto ? (
                            <Image
                              source={{ uri: prod.foto }}
                              style={styles.productImageHorizontal}
                              resizeMode="contain"
                            />
                          ) : (
                            <View style={styles.placeholderImageHorizontal}>
                              <MaterialCommunityIcons name="image-outline" size={50} color={colores.grisClaro} />
                            </View>
                          )}

                          {/* Badge de cantidad obligatoria */}
                          {getCantOblig(prod.plu) > 1 && (
                            <View style={styles.cantObligBadge}>
                              <Text style={styles.cantObligText}>x{getCantOblig(prod.plu)}</Text>
                            </View>
                          )}

                          {/* Información del producto */}
                          <View style={styles.productInfoHorizontal}>
                            <Text style={styles.productNameHorizontal} numberOfLines={2}>
                              {prod.descripcion || prod.nombre || ''}
                            </Text>
                            
                            {/* Mostrar cantidad requerida */}
                            <Text style={styles.cantObligInfoText}>
                              {getCantOblig(prod.plu) > 1 
                                ? `Llevás ${getCantOblig(prod.plu)} unidades` 
                                : '1 unidad'}
                            </Text>

                          </View>
                        </View>

                        {/* Divisor con + en el medio (solo entre productos) */}
                        {index === 0 && productos.length > 1 && (
                          <View style={[
                            styles.dividerContainer,
                            isSmallWeb && styles.dividerContainerVertical
                          ]}>
                            <View style={[
                              styles.dividerLine,
                              isSmallWeb && styles.dividerLineHorizontal
                            ]} />
                            <View style={styles.plusCircle}>
                              <Text style={styles.plusText}>+</Text>
                            </View>
                            <View style={[
                              styles.dividerLine,
                              isSmallWeb && styles.dividerLineHorizontal
                            ]} />
                          </View>
                        )}
                      </React.Fragment>
                    );
                  })}
                </View>
              </View>
            )}
          </ScrollView>

          {/* Controles de cantidad y botón de agregar al carrito */}
          <View style={styles.actionButton}>
            {/* Selector de cantidad */}
            <View style={styles.quantityContainer}>
              <Text style={styles.quantityLabel}>Cantidad de combos:</Text>
              <View style={styles.quantityControls}>
                <TouchableOpacity 
                  style={styles.quantityButton}
                  onPress={decrementarCantidad}
                  disabled={cantidad <= 1}
                >
                  <Text style={[styles.quantityButtonText, cantidad <= 1 && styles.quantityButtonDisabled]}>−</Text>
                </TouchableOpacity>
                
                <View style={styles.quantityDisplay}>
                  <Text style={styles.quantityText}>{cantidad}</Text>
                </View>
                
                <TouchableOpacity 
                  style={[styles.quantityButton, { borderRightWidth: 0 }]}
                  onPress={incrementarCantidad}
                >
                  <Text style={styles.quantityButtonText}>+</Text>
                </TouchableOpacity>
              </View>
            </View>
          
            {/* Botón CTA */}
            <TouchableOpacity
              style={[styles.actionCtaButton, styles.actionCtaEnabled]}
              onPress={agregarProductoConRegalo}
              disabled={agregandoCarrito}
            >
              <Text style={styles.actionCtaText}>
                {agregandoCarrito ? 'AGREGANDO...' : 'AGREGAR AL CARRITO'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
      
      {/* Modal de Login cuando el usuario no está autenticado */}
      <Login visible={showLogin} onClose={() => setShowLogin(false)} />
      
      {/* Modal de feedback para errores */}
      <FeedbackModal
        visible={feedbackModal.visible}
        type={feedbackModal.type}
        title={feedbackModal.title}
        message={feedbackModal.message}
        confirmText="Entendido"
        onClose={() => setFeedbackModal({ ...feedbackModal, visible: false })}
      />
    </Modal>
  );
};

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalView: {
    backgroundColor: colores.blanco,
    width: Platform.OS === 'web' ? '60%' : '90%',
    maxWidth: 600,
    maxHeight: '90%',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }
      : {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 5,
      }
    ),
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    backgroundColor: colores.rojo,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
  },
  modalTitle: {
    color: colores.blanco,
    fontSize: 18,
    fontWeight: 'bold',
  },
  closeButton: {
    padding: 5,
  },

  comboTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  comboDescription: {
    fontSize: 14,
    color: colores.textoSecundario,
  },
  progressContainer: {
    marginTop: 8,
  },
  progressBarBg: {
    width: '100%',
    height: 8,
    backgroundColor: '#EAEAEA',
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: colores.rojo,
  },
  progressText: {
    marginTop: 6,
    fontSize: 12,
    color: colores.textoSecundario,
  },
  statusReady: {
    color: colores.verde,
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginVertical: 10,
    paddingVertical: 5,
    paddingHorizontal: 15,
    borderBottomWidth: 1,
    borderBottomColor: colores.grisClaro,
    textAlign: 'center',
  },
  currentProductContainer: {
  },
  scrollContainer: {
    flex: 1,
  },
  productCard: {
    flexDirection: 'row',
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: colores.grisClaro,
    marginHorizontal: 15,
  },
  productImage: {
    width: 80,
    height: 80,
    marginRight: 10,
  },
  placeholderImage: {
    width: 80,
    height: 80,
    backgroundColor: '#f9f9f9',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  productInfo: {
    flex: 1,
    justifyContent: 'center',
  },
  productName: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 5,
    textTransform: 'uppercase',
  },
  productCategory: {
    fontSize: 12,
    color: colores.grisOscuro,
    marginBottom: 5,
  },
  pricesContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8,
    marginVertical: 5,
  },
  comboInfoCard: {
    backgroundColor: colores.grisClaro,
    padding: 10,
    marginHorizontal: 15,
    marginBottom: 15,
    borderRadius: 5,
    borderLeftWidth: 4,
    borderLeftColor: colores.rojo,
  },
  comboDetailText: {
    fontSize: 14,
    color: colores.textoSecundario,
  },
  stockText: {
    fontSize: 12,
    color: colores.verde,
    marginTop: 5,
  },
  cucardaContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 40,
    height: 40,
    zIndex: 2,
  },
  cucardaImage: {
    width: '100%',
    height: '100%',
  },
  productPrice: {
    fontSize: 16,
    color: colores.rojo,
    fontWeight: 'bold',
    flexShrink: 1,
  },
  priceTag: {
    backgroundColor: colores.rojo,
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginTop: 5,
    alignSelf: 'flex-start',
  },
  priceTagText: {
    color: colores.blanco,
    fontSize: 12,
    fontWeight: 'bold',
  },
  infoText: {
    fontSize: 12,
    color: colores.textoSecundario,
    marginBottom: 10,
    paddingHorizontal: 15,
  },
  errorText: {
    fontSize: 14,
    color: colores.rojo,
    textAlign: 'center',
    marginVertical: 10,
  },
  loadingText: {
    fontSize: 14,
    color: colores.textoSecundario,
    textAlign: 'center',
    marginTop: 10,
  },
  loadingContainer: {
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  messageContainer: {
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  retryButton: {
    backgroundColor: colores.rojo,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 5,
    marginTop: 15,
  },
  retryButtonText: {
    color: colores.blanco,
    fontWeight: 'bold',
  },
  cantidadContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
    marginBottom: 5,
  },
  cantidadButton: {
    backgroundColor: colores.rojo,
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 4,
  },
  cantidadButtonDisabled: {
    backgroundColor: colores.grisClaro,
  },
  cantidadButtonText: {
    color: colores.blanco,
    fontSize: 18,
    fontWeight: 'bold',
  },
  cantidadInput: {
    width: 40,
    height: 30,
    borderWidth: 1,
    borderColor: colores.grisClaro,
    textAlign: 'center',
    marginHorizontal: 5,
    borderRadius: 4,
    fontSize: 14,
  },
  agregarButton: {
    backgroundColor: colores.verde,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    marginLeft: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  agregarButtonText: {
    color: colores.blanco,
    fontSize: 12,
    fontWeight: 'bold',
  },
  actionButton: {
    padding: 15,
    flexDirection: 'column',
    alignItems: 'center',
    gap: 15,
  },
  quantityContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
  },
  quantityLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colores.textoPrincipal,
  },
  quantityControls: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#000',
    borderRadius: 4,
    overflow: 'hidden',
  },
  quantityButton: {
    width: 40,
    height: 40,
    backgroundColor: colores.blanco,
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderColor: '#000',
    ...(Platform.OS === 'web' ? {
      cursor: 'pointer',
    } : {}),
  },
  quantityButtonText: {
    color: '#000',
    fontSize: 18,
    fontWeight: 'normal',
  },
  quantityButtonDisabled: {
    opacity: 0.3,
  },
  quantityDisplay: {
    width: 80,
    height: 40,
    backgroundColor: colores.blanco,
    borderRightWidth: 1,
    borderLeftWidth: 0,
    borderColor: '#000',
    alignItems: 'center',
    justifyContent: 'center',
  },
  quantityText: {
    fontSize: 16,
    fontWeight: 'normal',
    color: '#000',
  },
  actionCtaButton: {
    alignSelf: 'center',
    paddingVertical: 12,
    paddingHorizontal: 30,
    borderRadius: 6,
    minWidth: 200,
  },
  actionCtaText: {
    color: colores.blanco,
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  actionCtaEnabled: {
    backgroundColor: colores.rojo,
  },
  actionCtaDisabled: {
    backgroundColor: colores.grisClaro,
  },
  giftProductContainer: {
    padding: 10,
  },
  horizontalProductsContainer: {
    flexDirection: 'row',
    alignItems: 'normal',
    justifyContent: 'center',
    width: '100%',
  },
  verticalProductsContainer: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  productCardHorizontal: {
    flex: 1,
    padding: 15,
    alignItems: 'center',
    position: 'relative',
  },
  productCardVertical: {
    width: '100%',
    marginVertical: 5,
  },
  productImageHorizontal: {
    width: 120,
    height: 120,
    marginVertical: 10,
  },
  placeholderImageHorizontal: {
    width: 120,
    height: 120,
    backgroundColor: colores.fondoClaro,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    marginVertical: 10,
  },
  productInfoHorizontal: {
    width: '100%',
    alignItems: 'center',
  },
  productNameHorizontal: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colores.textoPrincipal,
    marginBottom: 5,
    textAlign: 'center',
  },
  productPriceHorizontal: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colores.rojo,
    marginVertical: 5,
  },
  gratisText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'green',
    marginVertical: 5,
    textAlign: 'center',
  },
  dividerContainer: {
    width: 60,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 10,
  },
  dividerContainerVertical: {
    width: '100%',
    height: 60,
    marginVertical: 10,
    marginHorizontal: 0,
    flexDirection: 'row',
  },
  dividerLine: {
    width: 2,
    height: 60,
    backgroundColor: colores.grisClaro,
  },
  dividerLineHorizontal: {
    width: '45%',
    height: 2,
  },
  plusCircle: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: colores.blanco,
    borderWidth: 3,
    borderColor: colores.rojo,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.15,
          shadowRadius: 4,
          elevation: 4,
        }
    ),
  },
  plusText: {
    fontSize: 32,
    fontWeight: 'bold',
    color: colores.rojo,
    lineHeight: 32,
  },
  giftBadge: {
    backgroundColor: 'green',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignSelf: 'center',
    marginBottom: 10,
  },
  mainProductBadge: {
    backgroundColor: colores.rojo,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignSelf: 'center',
    marginBottom: 10,
  },
  badgeText: {
    color: colores.blanco,
    fontSize: 11,
    fontWeight: 'bold',
  },
  comboWideBadge: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  comboWideBadgeText: {
    backgroundColor: colores.rojo,
    color: colores.blanco,
    fontWeight: 'bold',
    fontSize: 12,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    textAlign: 'center',
  },
  giftTag: {
    backgroundColor: 'green',
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 4,
    marginTop: 5,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  giftTagText: {
    color: colores.blanco,
    fontSize: 12,
    fontWeight: 'bold',
    marginLeft: 4,
  },
  giftInfoText: {
    fontSize: 12,
    color: colores.textoSecundario,
    marginTop: 10,
    fontStyle: 'italic',
  },
  cantObligBadge: {
    position: 'absolute',
    top: 5,
    right: 5,
    backgroundColor: colores.rojo,
    borderRadius: 15,
    paddingVertical: 4,
    paddingHorizontal: 10,
    zIndex: 10,
  },
  cantObligText: {
    color: colores.blanco,
    fontSize: 14,
    fontWeight: 'bold',
  },
  cantObligInfoText: {
    fontSize: 12,
    color: colores.rojo,
    fontWeight: '600',
    marginTop: 5,
  },
  productBrand: {
    fontSize: 13,
    color: colores.textoSecundario,
    marginTop: 3,
    fontWeight: '600',
  },
});

export default ComboPrecioFijo;
